/**
 * Data Management Page
 *
 * Super Admin functionality for managing site data:
 * - View data statistics across tables
 * - Clean up / reset product catalog with confirmation
 *
 * Date: January 11, 2026
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Loader2,
  Trash2,
  Database,
  AlertTriangle,
  CheckCircle,
  RefreshCw,
  Shield,
  Calendar
} from 'lucide-react';
import { authFetch } from '@/utils/auth-fetch';
import { useSite } from '@/contexts/SiteContext';

interface TableStat {
  table_name: string;
  display_name: string;
  description: string;
  count: number;
}

interface StatsData {
  site: {
    id: string;
    name: string;
  };
  stats: TableStat[];
  dateRange: {
    oldest: string | null;
    newest: string | null;
  };
  totalRecords: number;
}

interface CleanupResult {
  success: boolean;
  message: string;
  deletedCounts: Record<string, number>;
  totalDeleted: number;
  preservedHistory: boolean;
}

export default function DataManagement() {
  const { selectedSite } = useSite();
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState<StatsData | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Cleanup dialog state
  const [showCleanupDialog, setShowCleanupDialog] = useState(false);
  const [confirmationInput, setConfirmationInput] = useState('');
  const [preserveHistory, setPreserveHistory] = useState(true);
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [cleanupResult, setCleanupResult] = useState<CleanupResult | null>(null);

  useEffect(() => {
    if (selectedSite?.id) {
      loadStats();
    }
  }, [selectedSite?.id]);

  const loadStats = async () => {
    if (!selectedSite?.id) return;

    setLoading(true);
    setError(null);

    try {
      const response = await authFetch(`/api/admin/data-management/${selectedSite.id}/stats`);
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Failed to load stats');
      }
      const data = await response.json();
      setStats(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  const handleCleanup = async () => {
    if (!selectedSite?.id || !stats) return;

    setCleanupLoading(true);
    setCleanupResult(null);

    try {
      const response = await authFetch(`/api/admin/data-management/${selectedSite.id}/cleanup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          confirmationToken: confirmationInput,
          preserveHistory
        })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || result.message || 'Cleanup failed');
      }

      setCleanupResult(result);
      setShowCleanupDialog(false);
      setConfirmationInput('');

      // Reload stats after successful cleanup
      setTimeout(loadStats, 1000);

    } catch (err) {
      setCleanupResult({
        success: false,
        message: err instanceof Error ? err.message : 'Unknown error',
        deletedCounts: {},
        totalDeleted: 0,
        preservedHistory: false
      });
    } finally {
      setCleanupLoading(false);
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const isConfirmationValid = stats && confirmationInput.toLowerCase() === stats.site.name.toLowerCase();

  if (!selectedSite) {
    return (
      <div className="p-6">
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>No Site Selected</AlertTitle>
          <AlertDescription>Please select a site from the header to manage its data.</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-red-100 rounded-lg">
            <Database className="h-6 w-6 text-red-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Data Management</h1>
            <p className="text-muted-foreground">
              View and manage product catalog data for {selectedSite.name}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="destructive" className="flex items-center gap-1">
            <Shield className="h-3 w-3" />
            Super Admin Only
          </Badge>
          <Button variant="outline" size="sm" onClick={loadStats} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Cleanup Result */}
      {cleanupResult && (
        <Alert variant={cleanupResult.success ? 'default' : 'destructive'}>
          {cleanupResult.success ? (
            <CheckCircle className="h-4 w-4" />
          ) : (
            <AlertTriangle className="h-4 w-4" />
          )}
          <AlertTitle>{cleanupResult.success ? 'Cleanup Complete' : 'Cleanup Failed'}</AlertTitle>
          <AlertDescription>{cleanupResult.message}</AlertDescription>
        </Alert>
      )}

      {/* Loading State */}
      {loading && !stats && (
        <Card>
          <CardContent className="py-12 flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            <span className="ml-2 text-muted-foreground">Loading data statistics...</span>
          </CardContent>
        </Card>
      )}

      {/* Stats Display */}
      {stats && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Total Records
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{stats.totalRecords.toLocaleString()}</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1">
                  <Calendar className="h-4 w-4" />
                  Oldest Record
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-xl font-semibold">{formatDate(stats.dateRange.oldest)}</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1">
                  <Calendar className="h-4 w-4" />
                  Newest Record
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-xl font-semibold">{formatDate(stats.dateRange.newest)}</div>
              </CardContent>
            </Card>
          </div>

          {/* Data Table */}
          <Card>
            <CardHeader>
              <CardTitle>Data by Table</CardTitle>
              <CardDescription>
                Breakdown of records stored for {stats.site.name}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Table</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead className="text-right">Records</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {stats.stats.map((stat) => (
                    <TableRow key={stat.table_name}>
                      <TableCell className="font-medium">{stat.display_name}</TableCell>
                      <TableCell className="text-muted-foreground">{stat.description}</TableCell>
                      <TableCell className="text-right">
                        <Badge variant={stat.count > 0 ? 'secondary' : 'outline'}>
                          {stat.count.toLocaleString()}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Danger Zone */}
          <Card className="border-red-200 bg-red-50">
            <CardHeader>
              <CardTitle className="text-red-700 flex items-center gap-2">
                <AlertTriangle className="h-5 w-5" />
                Danger Zone
              </CardTitle>
              <CardDescription className="text-red-600">
                These actions are destructive and cannot be undone
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between p-4 border border-red-200 rounded-lg bg-white">
                <div>
                  <h4 className="font-semibold text-red-700">Reset Product Catalog</h4>
                  <p className="text-sm text-muted-foreground">
                    Delete all products, AEO generations, and related data for this site.
                    You can optionally preserve enrichment job history for cost tracking.
                  </p>
                </div>
                <Button
                  variant="destructive"
                  onClick={() => setShowCleanupDialog(true)}
                  disabled={stats.totalRecords === 0}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Reset Catalog
                </Button>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Cleanup Confirmation Dialog */}
      <Dialog open={showCleanupDialog} onOpenChange={setShowCleanupDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Confirm Catalog Reset
            </DialogTitle>
            <DialogDescription>
              This action will permanently delete all product data for <strong>{stats?.site.name}</strong>.
              This cannot be undone.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Records to be deleted */}
            <div className="p-3 bg-red-50 rounded-lg border border-red-200">
              <p className="text-sm font-medium text-red-700 mb-2">
                The following will be deleted:
              </p>
              <ul className="text-sm text-red-600 space-y-1">
                {stats?.stats.filter(s => s.count > 0).map(s => (
                  <li key={s.table_name}>
                    {s.count.toLocaleString()} {s.display_name.toLowerCase()}
                  </li>
                ))}
              </ul>
            </div>

            {/* Preserve history option */}
            <div className="flex items-center space-x-2">
              <Checkbox
                id="preserveHistory"
                checked={preserveHistory}
                onCheckedChange={(checked) => setPreserveHistory(checked === true)}
              />
              <Label htmlFor="preserveHistory" className="text-sm">
                Preserve enrichment job history (for cost tracking)
              </Label>
            </div>

            {/* Confirmation input */}
            <div className="space-y-2">
              <Label htmlFor="confirmation">
                Type <strong>{stats?.site.name}</strong> to confirm:
              </Label>
              <Input
                id="confirmation"
                value={confirmationInput}
                onChange={(e) => setConfirmationInput(e.target.value)}
                placeholder={stats?.site.name}
                className={isConfirmationValid ? 'border-green-500' : ''}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowCleanupDialog(false);
                setConfirmationInput('');
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleCleanup}
              disabled={!isConfirmationValid || cleanupLoading}
            >
              {cleanupLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete All Data
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
